'use client'

import { useEffect, useMemo } from 'react'

import { sanitizeWithDOMPurify } from '@/lib/dompurify'
import { sanitizeHtmlValue, sanitizeUrlValue } from '@/lib/element-schema-sanitizers'
import { applyTenantPrimaryTheme } from '@/lib/tenant-theme-colors'
import { useBranding } from '@/hooks/use-branding'
import { useTenantConfig } from '@/hooks/use-tenant-config'

/**
 * Component to apply tenant-specific theming
 * Injects CSS variables and custom styles based on tenant config
 */
export function TenantTheme() {
  const { theme, primaryColor, secondaryColor } = useTenantConfig()
  const { isFsdsDomain } = useBranding()

  useEffect(() => {
    const root = document.documentElement
    // Apply .theme-non-fsds at the root level so portaled components
    // (Dialog, AlertDialog, Sheet, DropdownMenu, etc.) inherit the blue
    // primary instead of falling back to :root red.
    if (!isFsdsDomain) {
      root.classList.add('theme-non-fsds')
    } else {
      root.classList.remove('theme-non-fsds')
    }
    // Only override CSS when a tenant primary is configured; FSDS uses :root red, communities use .theme-non-fsds blue.
    applyTenantPrimaryTheme(root, primaryColor ?? null)

    if (secondaryColor) {
      root.style.setProperty('--secondary-color', secondaryColor)
    } else {
      root.style.removeProperty('--secondary-color')
    }

    if (theme) {
      Object.entries(theme).forEach(([key, value]) => {
        if (typeof value === 'string' && key !== 'logo' && key !== 'primaryColor') {
          const cssVar = `--tenant-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`
          root.style.setProperty(cssVar, value)
        }
      })
    }

    return () => {
      root.classList.remove('theme-non-fsds')
      applyTenantPrimaryTheme(root, null)
    }
  }, [theme, primaryColor, secondaryColor, isFsdsDomain])

  return null
}

/**
 * Component to inject tenant custom CSS
 */
export function TenantCustomCSS() {
  const { customization } = useTenantConfig()
  const safeCustomCSS = useMemo(
    () => (customization?.customCSS || '').replace(/<\/style/gi, '<\\/style'),
    [customization?.customCSS]
  )

  if (!safeCustomCSS) {
    return null
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: safeCustomCSS,
      }}
    />
  )
}

/**
 * Component to inject tenant custom head HTML (analytics, fonts, etc.)
 */
export function TenantCustomHead() {
  const { customization } = useTenantConfig()

  useEffect(() => {
    if (!customization?.customHeadHTML) {
      return
    }

    const sanitizedHeadHTML = sanitizeWithDOMPurify(customization.customHeadHTML, {
      ALLOWED_TAGS: ['meta', 'link', 'style', 'script'],
      ALLOWED_ATTR: [
        'name',
        'content',
        'property',
        'http-equiv',
        'charset',
        'rel',
        'href',
        'as',
        'media',
        'crossorigin',
        'integrity',
        'referrerpolicy',
        'type',
        'src',
        'async',
        'defer',
        'id',
        'class',
        'nonce',
      ],
      ALLOW_DATA_ATTR: false,
    })

    // Create a temporary container to parse sanitized HTML
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = sanitizedHeadHTML

    // Move each element to the head
    const head = document.head
    const elements = Array.from(tempDiv.children)
    const injectedElements: HTMLElement[] = []

    elements.forEach(element => {
      // Clone the element to avoid removing it from tempDiv
      const cloned = element.cloneNode(true) as HTMLElement
      const tagName = cloned.tagName.toLowerCase()

      // Block inline script payloads; allow only external scripts with safe src
      if (tagName === 'script') {
        const src = cloned.getAttribute('src') || ''
        const safeSrc = sanitizeUrlValue(src)
        if (!safeSrc) {
          return
        }
        cloned.setAttribute('src', safeSrc)
        while (cloned.firstChild) {
          cloned.removeChild(cloned.firstChild)
        }
      }

      if (tagName === 'link') {
        const href = cloned.getAttribute('href') || ''
        const safeHref = sanitizeUrlValue(href)
        if (!safeHref) {
          return
        }
        cloned.setAttribute('href', safeHref)
      }

      cloned.setAttribute('data-tenant-injected', 'true')
      head.appendChild(cloned)
      injectedElements.push(cloned)
    })

    // Cleanup function to remove injected elements
    return () => {
      injectedElements.forEach(el => {
        if (el.parentNode === head) {
          head.removeChild(el)
        }
      })
    }
  }, [customization?.customHeadHTML])

  return null
}

/**
 * Component to inject tenant custom body HTML (widgets, footer, etc.)
 */
export function TenantCustomBody() {
  const { customization } = useTenantConfig()
  const safeCustomBodyHTML = useMemo(
    () => sanitizeHtmlValue(customization?.customBodyHTML || ''),
    [customization?.customBodyHTML]
  )

  if (!safeCustomBodyHTML) {
    return null
  }

  return (
    <div
      dangerouslySetInnerHTML={{
        __html: safeCustomBodyHTML,
      }}
    />
  )
}
